import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trash2, UploadCloud, Pencil, FileText, ImageIcon, VideoIcon } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { useFile } from '../../../services/FileService';
import { isRenderableImage, isPlayableVideo } from '../../../utils/media-types';
import { describeAccept, formatFileSize } from '../../../utils/file-validation';
import UploadErrors from '../../../components/UploadErrors';
import { renameToMatchType } from './fileTypeNaming';
import './FileUpload.scss';

// Helper component to render existing files with signed URLs
const SignedMediaThumbnail = ({ 
  file, 
  enableFileNameEdit, 
  isEditing, 
  editingFileName, 
  onStartEdit, 
  onUpdateName, 
  onCancelEdit,
  onFileNameChange 
}) => {
  const { signedUrl, loading, error } = useFile(file.url || file.preview);
  // Only what the browser can draw or play — see utils/media-types.
  const itemType = file.contentType || file.type || '';
  const isImage = isRenderableImage(itemType);
  const isVideo = isPlayableVideo(itemType);

  const fileName = file.displayName || file.name || 'Unknown file';
  
  if (loading) {
    return (
      <div className="file-item-doc">
        <FileText size={48} className="file-item-doc-icon" />
        <span className="file-item-doc-name">{fileName} (טוען...)</span>
      </div>
    );
  }

  if (error) {
    console.error('Error loading signed URL:', file.url, error);
    return (
      <div className="file-item-doc">
        <FileText size={48} className="file-item-doc-icon" />
        <span className="file-item-doc-name">{fileName} (שגיאה) ⚠️</span>
      </div>
    );
  }

  if (isImage) {
    return (
      <>
        <img 
          src={signedUrl} 
          alt={fileName} 
          className="file-item-image"
        />
        {enableFileNameEdit && (
          <div className="file-name-editor">
            {isEditing ? (
              <input
                type="text"
                value={editingFileName}
                onChange={(e) => onFileNameChange(e.target.value)}
                onBlur={() => onUpdateName(editingFileName)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onUpdateName(editingFileName);
                    e.target.blur();
                  }
                  if (e.key === 'Escape') {
                    onCancelEdit();
                  }
                }}
                className="edit-input"
                autoFocus
              />
            ) : (
              <div className="file-name-wrapper">
                <span className="file-name" title={fileName}>
                  {fileName}
                </span>
                <button 
                  type="button" 
                  className="edit-btn"
                  onClick={onStartEdit}
                  title="ערוך שם קובץ"
                >
                  <Pencil size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </>
    );
  } else if (isVideo) {
    return (
      <>
        <video 
          controls
          src={signedUrl} 
          className="file-item-video"
        >
          <source src={signedUrl} type={itemType} />
          הדפדפן שלך אינו תומך בתגית הווידאו.
        </video>
        {enableFileNameEdit && (
          <div className="file-name-editor">
            {isEditing ? (
              <input
                type="text"
                value={editingFileName}
                onChange={(e) => onFileNameChange(e.target.value)}
                onBlur={() => onUpdateName(editingFileName)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onUpdateName(editingFileName);
                    e.target.blur();
                  }
                  if (e.key === 'Escape') {
                    onCancelEdit();
                  }
                }}
                className="edit-input"
                autoFocus
              />
            ) : (
              <div className="file-name-wrapper">
                <span className="file-name" title={fileName}>
                  {fileName}
                </span>
                <button 
                  type="button" 
                  className="edit-btn"
                  onClick={onStartEdit}
                  title="ערוך שם קובץ"
                >
                  <Pencil size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </>
    );
  } else {
    return (
      <div className="file-item-doc">
        <FileText size={48} className="file-item-doc-icon" />
        {enableFileNameEdit ? (
          isEditing ? (
            <input
              type="text"
              value={editingFileName}
              onChange={(e) => onFileNameChange(e.target.value)}
              onBlur={() => onUpdateName(editingFileName)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onUpdateName(editingFileName);
                  e.target.blur();
                }
                if (e.key === 'Escape') {
                  onCancelEdit();
                }
              }}
              className="edit-input-doc"
              autoFocus
            />
          ) : (
            <div className="file-name-wrapper-doc">
              <span className="file-item-doc-name" title={fileName}>
                {fileName}
              </span>
              <button 
                type="button" 
                className="edit-btn-doc"
                onClick={onStartEdit}
                title="ערוך שם קובץ"
              >
                <Pencil size={14} />
              </button>
            </div>
          )
        ) : (
          <span className="file-item-doc-name">{fileName}</span>
        )}
      </div>
    );
  }
};


const FileUpload = ({ 
  onFilesChange, 
  initialFiles = [], 
  maxFiles = 5, 
  maxSize = 5 * 1024 * 1024, 
  accept = {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
  },
  compressImages = false,
  compressionThreshold = 250 * 1024,
  compressionOptions = {
    maxSizeMB: 0.1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.7,
    alwaysKeepResolution: false,
    fileType: 'image/jpeg',
  },
  dropzoneText = 'בחר קבצים או גרור לכאן',
  // Derived, not a hardcoded string: the old default advertised "PDF, JPEG or
  // PNG less than 5MB" on a picker that in fact took 22 formats up to 20MB.
  dropzoneSubtext = `${describeAccept(accept)} · עד ${formatFileSize(maxSize)} לקובץ`,
  onError = null,
  enableFileNameEdit = false,
  // null hides it, for a dialog whose title already says what this is.
  label = 'קבצים מצורפים',
}) => {
  const [files, setFiles] = useState([]);
  const [editingFile, setEditingFile] = useState(null);
  const [editingFileName, setEditingFileName] = useState('');
  // Rejected-file messages, owned here so every consumer gets the same inline,
  // self-clearing surface instead of an alert() or a red line that never goes.
  const [rejections, setRejections] = useState([]);

  useEffect(() => {
    const initialFileObjects = initialFiles.map(file => {
      if (typeof file === 'string') {
        const name = file.substring(file.lastIndexOf('/') + 1);
        const format = name.substring(name.lastIndexOf('.') + 1).toUpperCase();
        return {
          name,
          preview: file,
          isInitial: true,
          format,
          size: 0,
        };
      }
      // Handle existing protocol files
      if (file.sasUrl || file.url || file.relativeUrl) {
        const name = file.name || file.displayName || 'Unknown file';
        const format = name.substring(name.lastIndexOf('.') + 1).toUpperCase();
        const displayName = file.displayName || name.split('.').slice(0, -1).join('.');
        return {
          name: name,
          displayName: displayName,
          preview: file.sasUrl || file.url,
          isInitial: true,
          format,
          size: file.size || 0,
          originalFile: file,
        };
      }
      // Handle new files
      const name = file.name;
      const format = name.substring(name.lastIndexOf('.') + 1).toUpperCase();
      return Object.assign(file, {
        preview: URL.createObjectURL(file),
        format,
        displayName: file.displayName || name.split('.').slice(0, -1).join('.')
      });
    });
    setFiles(initialFileObjects);

    return () => initialFileObjects.forEach(file => {
      if (!file.isInitial && file.preview) URL.revokeObjectURL(file.preview)
    });
  }, [initialFiles]);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    let errorMessages = [];

    if (rejectedFiles && rejectedFiles.length > 0) {
      rejectedFiles.forEach(r => {
        if (r.errors.some(e => e.code === 'file-too-large')) {
          errorMessages.push(`הקובץ "${r.file.name}" גדול מדי (${formatFileSize(r.file.size)}). הגודל המרבי הוא ${formatFileSize(maxSize)}.`);
        } else if (r.errors.some(e => e.code === 'file-invalid-type')) {
          errorMessages.push(`הקובץ "${r.file.name}" מסוג שאינו נתמך.`);
        } else {
          errorMessages.push(`לא ניתן להוסיף את הקובץ "${r.file.name}".`);
        }
      });
    }

    const processedFiles = [];

    for (const file of acceptedFiles) {
      if (file.size > maxSize) {
        errorMessages.push(`הקובץ "${file.name}" גדול מדי (${formatFileSize(file.size)}). הגודל המרבי הוא ${formatFileSize(maxSize)}.`);
        continue;
      }

      // Empty files upload "successfully" and then render as a broken tile.
      if (file.size === 0) {
        errorMessages.push(`הקובץ "${file.name}" ריק ולא ניתן להוסיף אותו.`);
        continue;
      }

      if (compressImages && file.type.startsWith('image/') && file.size > compressionThreshold) {
        try {
          console.log(`Attempting to compress ${file.name}, original size: ${Math.round(file.size / 1024)}KB.`);
          const compressedFile = await imageCompression(file, compressionOptions);
          const compressedSizeKB = Math.round(compressedFile.size / 1024);
          console.log(`Compression result for ${file.name}: new size: ${compressedSizeKB}KB.`);

          if (compressedFile.size > 75 * 1024) {
            console.warn(`WARN: Compressed file ${file.name} is still ${compressedSizeKB}KB.`);
          }

          // Compression re-encodes to compressionOptions.fileType, so the
          // original extension would now describe the wrong bytes — a
          // compressed screenshot.png holds JPEG data. The server matches the
          // extension against the declared mimetype, and Azure derives the
          // blob's Content-Type from the extension, so the name has to follow
          // the new type.
          const finalName = renameToMatchType(file.name, compressedFile.type);
          const finalFile = new File([compressedFile], finalName, {
            type: compressedFile.type,
            lastModified: Date.now()
          });
          const format = finalName.substring(finalName.lastIndexOf('.') + 1).toUpperCase();
          processedFiles.push(Object.assign(finalFile, {
            preview: URL.createObjectURL(finalFile),
            format,
            displayName: file.name.split('.').slice(0, -1).join('.')
          }));
        } catch (compressionError) {
          // Not surfaced to the user: the file is still added, just uncompressed.
          // Showing red text next to a tile that appeared fine reads as failure.
          console.error(`Error during image compression for ${file.name}:`, compressionError);
          const format = file.name.substring(file.name.lastIndexOf('.') + 1).toUpperCase();
          processedFiles.push(Object.assign(file, {
            preview: URL.createObjectURL(file),
            format,
            displayName: file.name.split('.').slice(0, -1).join('.')
          }));
        }
      } else {
        const format = file.name.substring(file.name.lastIndexOf('.') + 1).toUpperCase();
        processedFiles.push(Object.assign(file, {
          preview: URL.createObjectURL(file),
          format,
          displayName: file.name.split('.').slice(0, -1).join('.')
        }));
      }
    }

    // Over the cap, keep what fits rather than discarding the whole drop —
    // throwing away six good files because the seventh did not fit means the
    // user has to find and re-pick all of them.
    const room = Math.max(0, maxFiles - files.length);
    const kept = processedFiles.slice(0, room);
    const dropped = processedFiles.slice(room);

    if (dropped.length > 0) {
      dropped.forEach(file => URL.revokeObjectURL(file.preview));
      errorMessages.push(
        room === 0
          ? `הגעת למכסה של ${maxFiles} קבצים. הסר קובץ כדי להוסיף אחר.`
          : `ניתן לצרף עד ${maxFiles} קבצים, לכן ${dropped.length} קבצים לא נוספו.`
      );
    }

    // Replace rather than append: the list always describes the latest attempt,
    // so a message about a file the user has since fixed cannot linger.
    setRejections(errorMessages);
    if (errorMessages.length > 0 && onError) {
      onError(errorMessages.join(' '));
    }

    if (kept.length === 0) return;

    const updatedFiles = [...files, ...kept];
    setFiles(updatedFiles);
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  }, [files, maxFiles, maxSize, compressImages, compressionThreshold, compressionOptions, onFilesChange, onError]);

  const isFull = files.length >= maxFiles;

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: accept,
    maxSize: maxSize,
    disabled: isFull,
  });

  const removeFile = (fileToRemove) => {
    const updatedFiles = files.filter(file => file !== fileToRemove);
    setFiles(updatedFiles);
    // Making room resolves a "reached the cap" complaint, so it should not
    // still be on screen afterwards.
    setRejections([]);
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
    if (!fileToRemove.isInitial && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
    }
  };

  const updateFileDisplayName = (fileIndex, newDisplayName) => {
    const updatedFiles = [...files];
    updatedFiles[fileIndex].displayName = newDisplayName;
    setFiles(updatedFiles);
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  };

  return (
    <div className="file-upload-component">
      {label && <div className="form-label">{label}</div>}
      <div
        {...getRootProps()}
        className={`dropzone${isDragActive ? ' active' : ''}${isDragReject ? ' reject' : ''}${isFull ? ' disabled' : ''}`}
        aria-label={dropzoneText}
      >
        <input {...getInputProps()} />
        <div className="dropzone-content">
          <div className="dropzone-text-container">
            <div>
              <p className="dropzone-title">
                {isFull ? `הגעת למכסה של ${maxFiles} קבצים` : dropzoneText}
              </p>
              <p className="dropzone-subtitle">
                {isFull ? 'הסר קובץ כדי להוסיף אחר' : dropzoneSubtext}
              </p>
            </div>
          </div>
          <UploadCloud className="upload-icon" size={37.75} />
        </div>
      </div>

      <UploadErrors
        messages={rejections}
        onDismiss={() => setRejections([])}
        hint={accept ? `סוגי קבצים נתמכים: ${describeAccept(accept)} · עד ${formatFileSize(maxSize)} לקובץ` : undefined}
      />

      {files.length > 0 && (
        <div className="file-display-section">
          {files.map((file, index) => {
            const isImage = isRenderableImage(file.type) || isRenderableImage(file.contentType);
            const isVideo = isPlayableVideo(file.type) || isPlayableVideo(file.contentType);
            const isExistingFile = file.isInitial && file.originalFile;
            
            return (
              <div key={index} className="file-item-container">
                <button 
                  type="button" 
                  className="remove-file-btn" 
                  onClick={() => removeFile(file)}
                  title="הסר קובץ"
                >
                  <Trash2 size={16} />
                </button>
                
                {/* Display based on file type and whether it's existing or new */}
                {isExistingFile ? (
                  <SignedMediaThumbnail 
                    file={file.originalFile}
                    enableFileNameEdit={enableFileNameEdit}
                    isEditing={editingFile === index}
                    editingFileName={editingFileName}
                    onStartEdit={() => {
                      setEditingFile(index);
                      const fileName = file.originalFile.displayName || file.originalFile.name || 'Unknown file';
                      const nameWithoutExt = fileName.includes('.') ? 
                        fileName.split('.').slice(0, -1).join('.') : fileName;
                      setEditingFileName(nameWithoutExt);
                    }}
                    onUpdateName={(newName) => {
                      updateFileDisplayName(index, newName);
                      setEditingFile(null);
                    }}
                    onCancelEdit={() => setEditingFile(null)}
                    onFileNameChange={setEditingFileName}
                  />
                ) : (
                  <>
                    {isImage && file.preview ? (
                      <>
                        <img 
                          src={file.preview} 
                          alt={file.displayName || file.name} 
                          className="file-item-image"
                        />
                        {/* File name with edit button for images */}
                        {enableFileNameEdit && (
                          <div className="file-name-editor">
                            {editingFile === index ? (
                              <input
                                type="text"
                                value={editingFileName}
                                onChange={(e) => setEditingFileName(e.target.value)}
                                onBlur={() => {
                                  updateFileDisplayName(index, editingFileName);
                                  setEditingFile(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateFileDisplayName(index, editingFileName);
                                    setEditingFile(null);
                                    e.target.blur();
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingFile(null);
                                  }
                                }}
                                className="edit-input"
                                autoFocus
                              />
                            ) : (
                              <div className="file-name-wrapper">
                                <span className="file-name" title={file.displayName || file.name}>
                                  {file.displayName || file.name || 'Unknown file'}
                                </span>
                                <button 
                                  type="button" 
                                  className="edit-btn"
                                  onClick={() => {
                                    setEditingFile(index);
                                    const fileName = file.displayName || file.name || 'Unknown file';
                                    const nameWithoutExt = fileName.includes('.') ? 
                                      fileName.split('.').slice(0, -1).join('.') : fileName;
                                    setEditingFileName(nameWithoutExt);
                                  }}
                                  title="ערוך שם קובץ"
                                >
                                  <Pencil size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : isVideo && file.preview ? (
                      <>
                        <video 
                          controls
                          src={file.preview} 
                          className="file-item-video"
                        >
                          <source src={file.preview} type={file.type} />
                          הדפדפן שלך אינו תומך בתגית הווידאו.
                        </video>
                        {/* File name with edit button for videos */}
                        {enableFileNameEdit && (
                          <div className="file-name-editor">
                            {editingFile === index ? (
                              <input
                                type="text"
                                value={editingFileName}
                                onChange={(e) => setEditingFileName(e.target.value)}
                                onBlur={() => {
                                  updateFileDisplayName(index, editingFileName);
                                  setEditingFile(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateFileDisplayName(index, editingFileName);
                                    setEditingFile(null);
                                    e.target.blur();
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingFile(null);
                                  }
                                }}
                                className="edit-input"
                                autoFocus
                              />
                            ) : (
                              <div className="file-name-wrapper">
                                <span className="file-name" title={file.displayName || file.name}>
                                  {file.displayName || file.name || 'Unknown file'}
                                </span>
                                <button 
                                  type="button" 
                                  className="edit-btn"
                                  onClick={() => {
                                    setEditingFile(index);
                                    const fileName = file.displayName || file.name || 'Unknown file';
                                    const nameWithoutExt = fileName.includes('.') ? 
                                      fileName.split('.').slice(0, -1).join('.') : fileName;
                                    setEditingFileName(nameWithoutExt);
                                  }}
                                  title="ערוך שם קובץ"
                                >
                                  <Pencil size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="file-item-doc">
                        <FileText size={48} className="file-item-doc-icon" />
                        {/* File name with edit button for documents */}
                        {enableFileNameEdit ? (
                          editingFile === index ? (
                            <input
                              type="text"
                              value={editingFileName}
                              onChange={(e) => setEditingFileName(e.target.value)}
                              onBlur={() => {
                                updateFileDisplayName(index, editingFileName);
                                setEditingFile(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updateFileDisplayName(index, editingFileName);
                                  setEditingFile(null);
                                  e.target.blur();
                                }
                                if (e.key === 'Escape') {
                                  setEditingFile(null);
                                }
                              }}
                              className="edit-input-doc"
                              autoFocus
                            />
                          ) : (
                            <div className="file-name-wrapper-doc">
                              <span className="file-item-doc-name" title={file.displayName || file.name}>
                                {file.displayName || file.name || 'Unknown file'}
                              </span>
                              <button 
                                type="button" 
                                className="edit-btn-doc"
                                onClick={() => {
                                  setEditingFile(index);
                                  const fileName = file.displayName || file.name || 'Unknown file';
                                  const nameWithoutExt = fileName.includes('.') ? 
                                    fileName.split('.').slice(0, -1).join('.') : fileName;
                                  setEditingFileName(nameWithoutExt);
                                }}
                                title="ערוך שם קובץ"
                              >
                                <Pencil size={14} />
                              </button>
                            </div>
                          )
                        ) : (
                          <span className="file-item-doc-name" title={file.displayName || file.name}>
                            {file.displayName || file.name || 'Unknown file'}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
